import styled from '@emotion/styled';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import {
  faListOl,
  faLocationDot,
  faMapLocationDot,
  faPenToSquare,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Images, ModulesStates } from '@racemap/sdk/schema/events';
import type { AnalyticsEvents } from '@racemap/utilities/analytics/registry';
import { eventTypeNameFromEventType } from '@racemap/utilities/consts/eventTypes';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { type FC, useEffect, useRef } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { attachPreviewHover } from 'src/lib/previewHover';
import { dateRangeLabel, httpsOnly, relativeLabel, statusOf } from './eventsOverview';
import { ShareEventButton } from './ShareEventButton';

const Card = styled.article<{ highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid ${(p) => (p.highlighted ? '#36739a' : '#dee2e6')};
  border-radius: 3.5px;
  overflow: hidden;
  transition: border-color 200ms ease;
  &:hover {
    border-color: #36739a;
  }
`;

const Media = styled.a`
  position: relative;
  display: block;
  height: 140px;
  background: #f2f2f2 center / cover no-repeat;
`;

/* Beside the title, not inside it: a button within an anchor is invalid. */
const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 5px;
`;

const PreviewImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Logo = styled.span`
  position: absolute;
  left: 15px;
  bottom: -28px;
  width: 64px;
  height: 64px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    max-width: 56px;
    max-height: 56px;
    object-fit: contain;
  }
`;

const Body = styled.div`
  padding: 38px 15px 15px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
`;

const Title = styled.a`
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  min-height: 42px;
  color: #202020;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  display: flex;
  gap: 7px;
  align-items: center;
  min-width: 0;
  font-size: 13px;
  color: #646464;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Links = styled.div`
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #f2f2f2;
  display: flex;
  flex-wrap: wrap;
  gap: 5px 15px;
  font-size: 13px;
  a {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

// fileName is a storage key, not a URL.
const bannerUrl = (imgs: Images | null | undefined) =>
  imgs?.large ?? imgs?.medium ?? imgs?.original ?? imgs?.small ?? null;

const athletes = (n: number) =>
  `${n.toLocaleString('en-US').replace(/,/g, ' ')} ${n === 1 ? 'athlete' : 'athletes'}`;

type Props = {
  event: RacemapEvent;
  highlighted?: boolean;
  onHover?: (id: string | null) => void;
};

export const EventCard: FC<Props> = ({ event, highlighted = false, onHover }) => {
  const status = statusOf(event);
  const slug = event.slug || event.id;
  const playerUrl = `/player/${slug}/`;
  const banner = bannerUrl(event.modules?.banner?.imageUrls);
  // The card's media shape, so the preview frames the whole track.
  const preview = `/api/events/${event.id}/preview?size=300x140&without_virtual_race_symbol`;

  // Without a banner the track preview shows, and hovering the card plays its animation.
  const cardRef = useRef<HTMLElement>(null);
  const overlayRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (banner != null || cardRef.current == null || overlayRef.current == null) return;
    return attachPreviewHover(cardRef.current, overlayRef.current, `${preview}&animated`);
  }, [banner, preview]);
  const results = event.modules?.timing?.state === ModulesStates.ACTIVATED;
  const track = (link: AnalyticsEvents['event_list_card_click']['link']) => () =>
    trackEvent('event_list_card_click', { link, status });
  const registration =
    status !== 'past' ? httpsOnly(event.modules?.registration?.externalRegistrationUrl) : null;

  return (
    <Card
      ref={cardRef}
      highlighted={highlighted}
      onMouseEnter={() => onHover?.(event.id)}
      onMouseLeave={() => onHover?.(null)}
    >
      <Media
        href={playerUrl}
        target="_blank"
        rel="noopener"
        onClick={track('image')}
        style={banner != null ? { backgroundImage: `url("${banner}")` } : undefined}
      >
        {banner == null && (
          <>
            <PreviewImg src={preview} alt="" loading="lazy" decoding="async" />
            <PreviewImg ref={overlayRef} alt="" />
          </>
        )}
        <Logo>
          <img src={`/api/events/${event.id}/logo`} alt="" loading="lazy" decoding="async" />
        </Logo>
      </Media>
      <Body>
        <TitleRow>
          <Title
            href={playerUrl}
            target="_blank"
            rel="noopener"
            title={event.name}
            onClick={track('title')}
          >
            {event.name}
          </Title>
          <ShareEventButton url={`${window.location.origin}${playerUrl}`} title={event.name} />
        </TitleRow>
        <Meta style={{ color: '#404040' }}>
          <FontAwesomeIcon icon={faCalendar} fixedWidth color="#646464" />
          <span>{dateRangeLabel(event)}</span>
          <span style={{ marginLeft: 'auto' }}>{relativeLabel(event)}</span>
        </Meta>
        <Meta>
          <FontAwesomeIcon icon={faLocationDot} fixedWidth />
          <span>{event.location || event.geoLocation?.placeName || '–'}</span>
        </Meta>
        <Meta>
          <FontAwesomeIcon icon={faUserGroup} fixedWidth />
          <span>
            {eventTypeNameFromEventType(event.eventType)} · {athletes(event.startersCount ?? 0)}
          </span>
        </Meta>
        <Links>
          <a
            href={playerUrl}
            target="_blank"
            rel="noopener"
            style={{ fontWeight: 600 }}
            onClick={track(status === 'past' ? 'replay' : 'map')}
          >
            <FontAwesomeIcon icon={faMapLocationDot} />
            {status === 'past' ? 'Replay' : 'Map'}
          </a>
          {results && (
            <a
              href={`/leaderboard/${slug}/`}
              target="_blank"
              rel="noopener"
              onClick={track('results')}
            >
              <FontAwesomeIcon icon={faListOl} />
              Results
            </a>
          )}
          {registration && (
            <a href={registration} target="_blank" rel="noopener" onClick={track('registration')}>
              <FontAwesomeIcon icon={faPenToSquare} />
              Registration
            </a>
          )}
        </Links>
      </Body>
    </Card>
  );
};
